import type { ReactNode } from 'react'

export function Spinner({ label }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-subtle-light dark:text-subtle-dark">
      <span className="material-icons animate-spin text-xl">sync</span>
      {label && <span className="text-sm">{label}</span>}
    </div>
  )
}

export function EmptyState({
  icon = 'inbox',
  title,
  hint,
  action,
}: {
  icon?: string
  title: string
  hint?: string
  action?: ReactNode
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <span className="material-icons text-4xl text-subtle-light dark:text-subtle-dark mb-3">
        {icon}
      </span>
      <p className="text-sm font-medium text-text-light dark:text-text-dark">{title}</p>
      {hint && <p className="mt-1 text-xs text-subtle-light dark:text-subtle-dark max-w-sm">{hint}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorState({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error)
  return (
    <div className="flex items-start gap-2 p-4 rounded-xl bg-error/10 text-error text-sm">
      <span className="material-icons text-base mt-0.5">error</span>
      <span>{message}</span>
    </div>
  )
}

// Convenience wrapper for react-query states.
export function QueryState({
  isLoading,
  error,
  isEmpty,
  emptyProps,
  children,
}: {
  isLoading: boolean
  error: unknown
  isEmpty?: boolean
  emptyProps?: Parameters<typeof EmptyState>[0]
  children: ReactNode
}) {
  if (isLoading) return <Spinner />
  if (error) return <ErrorState error={error} />
  if (isEmpty && emptyProps) return <EmptyState {...emptyProps} />
  return <>{children}</>
}
